/*
 * Назначение: вкладка «Напоминания» в настройках проекта.
 * Функционал: вкл/выкл и список дней (через запятую) для возвратных
 * сообщений клиентам. Расписание сохраняется в БД, отправка делается в
 * планировщике.
 */

import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import type { ProjectDetails } from "../../../lib/types";

import { useProjectMutation } from "./shared";

const parseDays = (raw: string): number[] => {
  const nums = raw
    .split(/[,\s]+/)
    .map((x) => parseInt(x, 10))
    .filter((n) => Number.isFinite(n) && n > 0 && n <= 365);
  return Array.from(new Set(nums)).sort((a, b) => a - b);
};

export function RemindersTab({ project }: { project: ProjectDetails }) {
  const mutation = useProjectMutation("сохранить напоминания");
  const [form, setForm] = useState({
    reminder_enabled: project.reminder_enabled,
    reminder_days: (project.reminder_days ?? [7, 14]).join(", "),
  });

  useEffect(() => {
    setForm({
      reminder_enabled: project.reminder_enabled,
      reminder_days: (project.reminder_days ?? [7, 14]).join(", "),
    });
  }, [project]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Напоминания</CardTitle>
        <CardDescription>
          Возвратные сообщения клиентам, которые не дошли до конверсии.
          Рассылка идёт автоматически раз в сутки в 10:00 по Москве. В Telegram
          и MAX сообщение приходит клиенту сразу, в чате на сайте — появляется
          в диалоге при следующем заходе.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Switch
            id="r-enabled"
            checked={form.reminder_enabled}
            onCheckedChange={(v) =>
              setForm((s) => ({ ...s, reminder_enabled: Boolean(v) }))
            }
          />
          <Label htmlFor="r-enabled">Напоминания включены</Label>
        </div>
        <div className="space-y-2">
          <Label htmlFor="r-days">Через сколько дней (через запятую)</Label>
          <Input
            id="r-days"
            value={form.reminder_days}
            onChange={(e) => setForm((s) => ({ ...s, reminder_days: e.target.value }))}
            placeholder="7, 14"
          />
          <p className="text-xs text-muted-foreground">
            ИИ напомнит клиенту через указанные интервалы, если диалог не закончился заявкой.
          </p>
        </div>
        <Button
          onClick={() =>
            mutation.mutate({
              reminder_enabled: form.reminder_enabled,
              reminder_days: parseDays(form.reminder_days),
            })
          }
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Сохранить
        </Button>
      </CardContent>
    </Card>
  );
}
